import instanceJWT, { dedupedGet } from "./apiJwt";
import type { SupplierInfoDto } from "../models/SupplierInfo";

export interface InstalledPluginDto {
  id: string | number;
  title: string;
  plugin_name: string;
  group: string | null;
  icon?: string;
  loaded?: string;
  wh_uuid?: string;
  /** Расширенные данные о поставщике (SER-1407). `null`, если бэк их не нашёл. */
  supplier_info?: SupplierInfoDto | null;
}

export interface PluginGroupDto {
  id: number;
  name: string;
  count: string;
  color: { R: number; G: number; B: number } | string;
  is_public: number;
  client_id?: number;
  profile_id?: number;
}

interface ApiEnvelope<T> {
  status: boolean;
  error: boolean | Record<string, unknown>;
  data: T;
}

function unwrap<T>(env: ApiEnvelope<T>): T {
  if (env.error && typeof env.error === "object") {
    const err = env.error as { message?: string; code?: number };
    throw new Error(err.message ?? "API error");
  }
  return env.data;
}

const BASE = "eplex/search/plugin_params";

/**
 * Лёгкий набор расширенных данных о поставщике: город, страна, график, срочная
 * доставка. Полный набор (`full`) весит в 11 раз больше и на списки не запрашивается —
 * бренды и категории берутся точечно через `suppliersApi`.
 */
export const SUPPLIER_INFO_PARAM = "supplier_info=1";

export async function listInstalledPlugins(): Promise<InstalledPluginDto[]> {
  const response = await dedupedGet(`${BASE}/plugins/installed?loaded=1&${SUPPLIER_INFO_PARAM}`);
  const env = response.data as ApiEnvelope<{ items: InstalledPluginDto[] }>;
  return unwrap(env).items ?? [];
}

export async function listWarehousePlugins(): Promise<InstalledPluginDto[]> {
  const response = await instanceJWT.get(`${BASE}/plugins/warehouse`);
  const env = response.data as ApiEnvelope<{ items: InstalledPluginDto[] }>;
  return unwrap(env).items ?? [];
}

export async function listPluginGroups(): Promise<PluginGroupDto[]> {
  const response = await instanceJWT.get(`${BASE}/groups/list`);
  const env = response.data as ApiEnvelope<{ items: PluginGroupDto[] }>;
  return unwrap(env).items ?? [];
}

/**
 * Что можно менять батчем у подключённого поставщика. Ручка умеет больше (title, group,
 * наценки, цвет…) — расширять по мере надобности, а не заранее.
 */
export interface InstalledPluginChangeDto {
  /** «Загружать в программу»: показывать поставщика в левой панели проценки. */
  loaded?: 0 | 1;
}

/** Так батч-ручка помечает поставщика, которого у клиента нет. */
const PLUGIN_NOT_FOUND = -1;

/**
 * Массовое изменение настроек подключённых поставщиков (`POST plugin_params/plugins`).
 *
 * В отличие от сохранения карточки (`PUT plugins/{id}`) бэк не ходит на сайт поставщика,
 * поэтому ручка работает и когда тот не отвечает — именно ею 1С отключает поставщика
 * с ошибкой (CAB-500). По каждому id бэк возвращает число: сколько записей затронуто,
 * `-1` — поставщик не найден.
 */
export async function changeInstalledPlugins(
  items: Record<string, InstalledPluginChangeDto>,
): Promise<Record<string, number>> {
  const response = await instanceJWT.post(`${BASE}/plugins`, { items });
  const env = response.data as ApiEnvelope<{ items?: Record<string, number> }>;
  return unwrap(env).items ?? {};
}

/** Признак «Загружать в программу» одного поставщика; `false` — бэк его не нашёл. */
export async function setPluginLoaded(pluginId: number, loaded: boolean): Promise<boolean> {
  const key = String(pluginId);
  const result = await changeInstalledPlugins({ [key]: { loaded: loaded ? 1 : 0 } });
  return (result[key] ?? PLUGIN_NOT_FOUND) !== PLUGIN_NOT_FOUND;
}
